// ── The Settings groups, in sidebar order ─────────────────────────────
//
// One list, read by three places that used to each carry their own copy:
//   - the sidebar in App.js, for the sub-items under Settings
//   - SettingsPage, to render the open group and decide what "unsaved" means
//   - the save bar, to say which group it is saving
//
// `keys` is what each group OWNS in the settings object. It answers "does this
// group have unsaved changes" by comparing those keys against what the server
// last returned, so editing a booking package never lights up Service Workflow.
// A key in no group would never look unsaved and never warn on the way out, so
// `check-settings-nav.js` asserts every writable key is claimed by exactly one
// group.
//
// `admin: true` is the Administrator-only set (17 Sep decision, see
// claude/settings-redesign-spec.md). The SERVER refuses these too -- this only
// keeps them out of the sidebar.
export const SETTINGS_GROUPS = [
  {
    id: 'shop', label: 'Shop', admin: true,
    blurb: 'Who you are and what you charge. Appears on customer emails and labor lines.',
    // closures (3 Nov): Closures and special hours, ClosuresSettings.jsx.
    keys: ['shopProfile', 'laborRate', 'closures'],
  },
  {
    id: 'connections', label: 'Connections', admin: true,
    blurb: 'The accounts Spinboard works through. Connecting, disconnecting and generating keys take effect immediately.',
    // Lightspeed, Stripe and the register keys act at once through their own
    // routes; `lightspeed` is the only part of this group that waits for Save.
    keys: ['lightspeed'],
  },
  {
    id: 'payments', label: 'Payments',
    blurb: 'How textable payment links behave once they are sent.',
    keys: ['payments'],
  },
  {
    id: 'booking', label: 'Online Booking',
    blurb: 'What customers can book online, and when.',
    keys: ['booking'],
  },
  {
    id: 'website', label: 'Website',
    blurb: 'Your shop website: its pages, the bikes it lists, and its photos. Changes here save as you make them; pages are edited in the page builder.',
    // Saves itself, action by action, through /api/website/admin/* (30 Oct).
    keys: [],
  },
  {
    id: 'messages', label: 'Messages',
    blurb: 'What customers receive by text, and what happens when they text after hours.',
    keys: ['smsTextTemplates', 'autoReply', 'customerFeedback'],
  },
  {
    id: 'workflow', label: 'Service Workflow',
    blurb: 'How tickets move through the shop: statuses, risk flags, checklists and manager review.',
    keys: ['statusCategories', 'excludedStatuses', 'techFinalizationStatuses',
      'staleThresholdDays', 'waitingThresholdDays', 'newTicketGraceDays',
      'highValueThreshold', 'sendbackReasons', 'emailClient', 'theme', 'techTime', 'quotes', 'partsOrder'],
  },
  {
    id: 'users', label: 'Users', admin: true,
    blurb: 'Who can sign in, and what each person can do.',
    // User Management saves itself, per user, through its own routes.
    keys: [],
  },
];

// What the Service Queue reads. A save that touches one of these gets the
// "the queue will show this next time it loads" line; a shop phone number or a
// register key does not, because a warning that fires on everything is ignored.
//
// `techFinalizationStatuses` is spelled the way the settings object spells it.
// It was `techFinalStatuses` here for a day (17 Sep) -- a name that exists
// nowhere, so the note silently never fired for the Tech Report statuses. A
// list of key names that is never compared against the real ones is exactly the
// kind of quiet wrong answer this codebase keeps finding.
export const QUEUE_KEYS = ['statusCategories', 'excludedStatuses', 'techFinalizationStatuses',
  'staleThresholdDays', 'waitingThresholdDays', 'newTicketGraceDays', 'highValueThreshold'];

// websiteOn (3 Nov): Settings › Website only for a shop Spinboard has switched
// it on for (server/helpers/websiteFeature.js).
export const groupsFor = (role, { websiteOn = false } = {}) =>
  SETTINGS_GROUPS.filter(g => (!g.admin || role === 'owner') && (g.id !== 'website' || websiteOn));

export const groupById = (id) => SETTINGS_GROUPS.find(g => g.id === id) || null;

// Has anything in this group changed since the server last confirmed a save?
export function groupIsDirty(group, settings, saved) {
  if (!group || !settings) return false;
  return group.keys.some(k => JSON.stringify(settings[k]) !== JSON.stringify(saved?.[k]));
}
